import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, TrendingUp, User, X, ChevronRight, ChevronLeft, Sparkles, Dumbbell, Utensils, Play, Trophy, Settings, Info } from "lucide-react";

type HighlightType = 'chat' | 'chat-input' | 'progress' | 'profile' | 'workout-button' | 'workout-plan' | 'nutrition-plan' | 'pr-section' | 'settings' | 'settings-button' | 'exercise-info-button';

interface TourStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  highlight?: HighlightType;
  position: 'center' | 'above-nav' | 'above-workout' | 'above-element';
  elementId?: string;
  showTapToExplore?: boolean;
  endsWithAction?: boolean;
}

const TOUR_STEPS: TourStep[] = [
  {
    title: "Welcome to Your Personal Coach!",
    description: "I'm here to help you achieve your fitness goals. Think of me as your personal trainer, nutritionist, and accountability partner all in one.",
    icon: <Sparkles className="w-8 h-8 text-primary" />,
    position: 'center',
  },
  {
    title: "Chat With Me Anytime",
    description: "Ask me anything! Request workout plans, nutrition advice, form tips, or just tell me about your day. I remember everything and personalize my coaching to you.",
    icon: <MessageCircle className="w-8 h-8 text-primary" />,
    highlight: "chat-input",
    position: 'above-element',
  },
  {
    title: "Your Workout Plans",
    description: "When I create a workout plan for you, it appears here. Tap it to see each day's exercises, sets, reps, and rest times laid out clearly.",
    icon: <Dumbbell className="w-8 h-8 text-primary" />,
    highlight: "workout-plan",
    position: 'above-element',
    elementId: 'workout-plan-card',
    showTapToExplore: true,
  },
  {
    title: "Exercise Instructions",
    description: "Look for the ⓘ button next to any exercise in your workout plan. Tap it to see a demonstration GIF and proper form instructions!",
    icon: <Info className="w-8 h-8 text-primary" />,
    position: 'center',
  },
  {
    title: "Your Nutrition Plan",
    description: "I'll also create a personalized meal plan based on your goals. Tap it to see daily meals, macros, and calorie targets.",
    icon: <Utensils className="w-8 h-8 text-primary" />,
    highlight: "nutrition-plan",
    position: 'above-element',
    elementId: 'nutrition-plan-card',
    showTapToExplore: true,
  },
  {
    title: "Track Your PRs",
    description: "Log your personal records here. I'll use your PRs to calculate suggested weights for every exercise in your workouts! Add one now or continue the tour.",
    icon: <Trophy className="w-8 h-8 text-primary" />,
    highlight: "pr-section",
    position: 'above-element',
    elementId: 'pr-section-header',
    showTapToExplore: true,
  },
  {
    title: "Your Profile & Settings",
    description: "This is your profile tab. Tap Settings to customize your experience, change themes, and update your goals.",
    icon: <User className="w-8 h-8 text-primary" />,
    highlight: "settings-button",
    position: 'above-element',
    elementId: 'settings-button',
  },
  {
    title: "Customize Your Experience",
    description: "This is Settings! Here you can change your app theme, adjust coaching style, update your goals, and manage your account preferences.",
    icon: <Settings className="w-8 h-8 text-primary" />,
    highlight: "settings",
    position: 'above-element',
    elementId: 'customize-tab-trigger',
  },
  {
    title: "Go Workout Mode",
    description: "Ready to train? Tap 'Go Workout' to start a guided session. You'll get AI-suggested weights based on your PRs, timers, rep tracking, and automatic PR detection!",
    icon: <Play className="w-8 h-8 text-primary" />,
    highlight: "workout-button",
    position: 'above-workout',
    showTapToExplore: true,
    endsWithAction: true,
  },
];

interface OnboardingTourProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  onTabChange?: (tab: 'chat' | 'progress' | 'profile') => void;
  onHighlightChange?: (highlight: 'workout' | 'nutrition' | null) => void;
  onSettingsToggle?: (open: boolean) => void;
  onWorkoutStart?: () => void;
  onWorkoutClose?: () => void;
  hasWorkoutPlan?: boolean;
  isExerciseGifModalOpen?: boolean;
}

export function OnboardingTour({ isOpen, onClose, onComplete, onTabChange, onHighlightChange, onSettingsToggle, onWorkoutStart, onWorkoutClose, hasWorkoutPlan, isExerciseGifModalOpen }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [elementRect, setElementRect] = useState<DOMRect | null>(null);
  const [isRepositioning, setIsRepositioning] = useState(false);
  const [chatInputRect, setChatInputRect] = useState<DOMRect | null>(null);
  const [workoutButtonRect, setWorkoutButtonRect] = useState<DOMRect | null>(null);
  const lastPositionRef = useRef<'top' | 'bottom' | null>(null);

  // Add/remove tour-active class on body for CSS scoping
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('tour-active');
      setCurrentStep(0);
      lastPositionRef.current = null;
    } else {
      document.body.classList.remove('tour-active');
    }
    return () => {
      document.body.classList.remove('tour-active');
    };
  }, [isOpen]);

  // Detect position changes and trigger fade animation
  useEffect(() => {
    if (!elementRect) return;
    
    const elementCenter = elementRect.top + elementRect.height / 2;
    const screenMiddle = window.innerHeight / 2;
    const newPosition: 'top' | 'bottom' = elementCenter > screenMiddle ? 'top' : 'bottom';
    
    if (lastPositionRef.current !== null && lastPositionRef.current !== newPosition) {
      // Position is changing, trigger fade
      setIsRepositioning(true);
      setTimeout(() => setIsRepositioning(false), 300);
    }
    
    lastPositionRef.current = newPosition;
  }, [elementRect]);

  // Navigate to the highlighted tab when step changes
  useEffect(() => {
    if (isOpen && onTabChange) {
      const step = TOUR_STEPS[currentStep];
      if (step.highlight === 'chat' || step.highlight === 'chat-input') {
        onTabChange('chat');
      } else if (step.highlight === 'profile' || step.highlight === 'settings' || step.highlight === 'settings-button') {
        onTabChange('profile');
      } else if (step.highlight === 'progress' || step.highlight === 'workout-plan' || step.highlight === 'nutrition-plan' || step.highlight === 'pr-section' || step.highlight === 'exercise-info-button') {
        onTabChange('progress');
      } else if (step.highlight === 'workout-button') {
        onTabChange('chat');
      }
    }
    
    // Notify about card highlight changes - don't expand workout card, just highlight it
    if (onHighlightChange) {
      const step = TOUR_STEPS[currentStep];
      if (step.highlight === 'exercise-info-button') {
        onHighlightChange('workout');
      } else if (step.highlight === 'nutrition-plan') {
        onHighlightChange('nutrition');
      } else {
        onHighlightChange(null);
      }
    }

    // Open/close settings modal (only when tour is open)
    if (isOpen && onSettingsToggle) {
      const step = TOUR_STEPS[currentStep];
      onSettingsToggle(step.highlight === 'settings');
    }
    
    // Close workout session when navigating to steps that need progress tab
    if (isOpen && onWorkoutClose) {
      const step = TOUR_STEPS[currentStep];
      if (step.highlight === 'nutrition-plan' || step.highlight === 'pr-section' || step.highlight === 'settings-button' || step.highlight === 'settings') {
        onWorkoutClose();
      }
    }
  }, [currentStep, isOpen, onTabChange, onHighlightChange, onSettingsToggle, onWorkoutClose]);

  // Track chat-input-container element position for accurate highlight
  useEffect(() => {
    if (!isOpen) return;
    
    const step = TOUR_STEPS[currentStep];
    if (step.highlight !== 'chat-input') {
      setChatInputRect(null);
      return;
    }

    const updateChatInputRect = () => {
      const el = document.getElementById('chat-input-container');
      if (el) {
        setChatInputRect(el.getBoundingClientRect());
      }
    };

    // Initial delay then update
    const timeout = setTimeout(updateChatInputRect, 100);
    window.addEventListener('resize', updateChatInputRect);
    window.addEventListener('scroll', updateChatInputRect, true);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', updateChatInputRect);
      window.removeEventListener('scroll', updateChatInputRect, true);
    };
  }, [currentStep, isOpen]);

  // Track go-workout-button element position for accurate highlight
  useEffect(() => {
    if (!isOpen) return;
    
    const step = TOUR_STEPS[currentStep];
    if (step.highlight !== 'workout-button') {
      setWorkoutButtonRect(null);
      return;
    }

    const updateWorkoutButtonRect = () => {
      const el = document.getElementById('go-workout-button');
      if (el) {
        setWorkoutButtonRect(el.getBoundingClientRect());
      }
    };

    // Initial delay then update
    const timeout = setTimeout(updateWorkoutButtonRect, 100);
    window.addEventListener('resize', updateWorkoutButtonRect);
    window.addEventListener('scroll', updateWorkoutButtonRect, true);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', updateWorkoutButtonRect);
      window.removeEventListener('scroll', updateWorkoutButtonRect, true);
    };
  }, [currentStep, isOpen]);

  // Auto-advance when ExerciseGifModal opens during exercise-info-button step
  useEffect(() => {
    if (!isOpen) return;
    
    const step = TOUR_STEPS[currentStep];
    if (step.highlight === 'exercise-info-button' && isExerciseGifModalOpen) {
      // Modal opened, auto-advance to next step
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 300);
    }
  }, [isOpen, currentStep, isExerciseGifModalOpen]);

  // Find and track the highlighted element's position
  useEffect(() => {
    if (!isOpen) return;
    
    const step = TOUR_STEPS[currentStep];
    if (!step.elementId) {
      setElementRect(null);
      return;
    }

    // Clear rect immediately when step changes so we don't show stale position
    setElementRect(null);

    const updateRect = () => {
      const el = document.getElementById(step.elementId!);
      if (el) {
        const rect = el.getBoundingClientRect();
        setElementRect(rect);
      } else {
        setElementRect(null);
      }
    };

    let retryCount = 0;
    const maxRetries = 5;
    
    const scrollAndHighlight = () => {
      const el = document.getElementById(step.elementId!);
      if (el) {
        // For settings button and customize tab, use scrollIntoView directly
        if (step.elementId === 'settings-button' || step.elementId === 'customize-tab-trigger') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(updateRect, 500);
          return;
        }
        
        // Find the scrollable parent container
        let scrollContainer: Element | null = el.parentElement;
        while (scrollContainer) {
          const style = window.getComputedStyle(scrollContainer);
          if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            break;
          }
          scrollContainer = scrollContainer.parentElement;
        }

        // Calculate scroll position to center the element
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          const scrollTop = scrollContainer.scrollTop;
          const targetScrollTop = scrollTop + elRect.top - containerRect.top - (containerRect.height / 2) + (elRect.height / 2);
          
          scrollContainer.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth'
          });
        } else {
          // Fallback to standard scrollIntoView
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        // Only show highlight AFTER scroll completes
        setTimeout(updateRect, 500);
      } else {
        // Element not found yet - retry with increasing delay
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(scrollAndHighlight, 200);
        }
      }
    };

    // Delay to let tab content render first, then scroll
    let delay = 200;
    if (step.elementId === 'customize-tab-trigger') delay = 500; // Wait for settings modal to open
    if (step.elementId === 'settings-button') delay = 400; // Wait for profile tab
    const timeout = setTimeout(scrollAndHighlight, delay);
    
    // Update rect on scroll after initial scroll completes
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateRect, 100);
    };
    window.addEventListener('scroll', handleScroll, true);
    
    // Also update on resize
    window.addEventListener('resize', updateRect);
    
    return () => {
      clearTimeout(timeout);
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [currentStep, isOpen]);

  if (!isOpen) return null;

  const step = TOUR_STEPS[currentStep];
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    // Close settings modal if we're leaving the settings step
    const currentStepData = TOUR_STEPS[currentStep];
    if (currentStepData.highlight === 'settings' && onSettingsToggle) {
      onSettingsToggle(false);
    }
    
    if (isLastStep) {
      onComplete();
    } else {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  // Block clicks on overlay - don't close tour
  const blockClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Get spotlight position for the highlighted tab
  const getSpotlightLeftPercent = () => {
    if (!step.highlight) return null;
    if (step.highlight === 'workout-button' || step.highlight === 'workout-plan' || step.highlight === 'nutrition-plan' || step.highlight === 'pr-section' || step.highlight === 'chat-input' || step.highlight === 'settings-button' || step.highlight === 'exercise-info-button') return null;
    const positions = {
      chat: 16.67,
      progress: 50,
      profile: 83.33,
    };
    return positions[step.highlight as 'chat' | 'progress' | 'profile'];
  };

  const spotlightLeft = getSpotlightLeftPercent();
  const isNavHighlight = step.highlight === 'chat' || step.highlight === 'progress' || step.highlight === 'profile';
  const isWorkoutHighlight = step.highlight === 'workout-button';
  const isChatInputHighlight = step.highlight === 'chat-input';
  const isElementHighlight = step.highlight === 'workout-plan' || step.highlight === 'nutrition-plan' || step.highlight === 'pr-section' || step.highlight === 'settings' || step.highlight === 'settings-button' || step.highlight === 'exercise-info-button';

  // Calculate card position and arrow direction
  const getCardPositionInfo = (): { style: React.CSSProperties; arrowPointsDown: boolean } => {
    if (step.position === 'center') {
      return { style: { bottom: '50%', transform: 'translateY(50%)' }, arrowPointsDown: true };
    }
    // Special positioning for chat-input - card at top since input is at bottom
    if (step.highlight === 'chat-input') {
      return { style: { top: '80px', bottom: 'auto' }, arrowPointsDown: true };
    }
    // Special positioning for settings - put card centered vertically
    if (step.highlight === 'settings') {
      return { style: { top: '50%', transform: 'translateY(-50%)', bottom: 'auto' }, arrowPointsDown: true };
    }
    if (step.position === 'above-element' && elementRect) {
      // Check if element is in the lower portion of the screen (below middle)
      const elementCenter = elementRect.top + elementRect.height / 2;
      const screenMiddle = window.innerHeight / 2;
      
      if (elementCenter > screenMiddle) {
        // Element is in lower half - position card at top of screen, arrow points DOWN to element
        return { style: { top: '80px', bottom: 'auto' }, arrowPointsDown: true };
      } else {
        // Element is in upper half - position card below element, arrow points UP to element
        const cardTop = elementRect.bottom + 24;
        return { style: { top: `${cardTop}px`, bottom: 'auto' }, arrowPointsDown: false };
      }
    }
    if (step.position === 'above-workout' && hasWorkoutPlan) {
      // Position the info box more centered on screen (higher up)
      return { style: { top: '35%', transform: 'translateY(-50%)', bottom: 'auto' }, arrowPointsDown: true };
    }
    return { style: { bottom: '5.5rem' }, arrowPointsDown: true };
  };

  const { style: cardStyle, arrowPointsDown } = getCardPositionInfo();

  return (
    <>
      {/* Main overlay - covers content area (skip when highlighting specific elements or chat-input) */}
      {!isElementHighlight && !isChatInputHighlight && (
        <div 
          className="fixed inset-x-0 top-0 bg-black/70 z-[100] transition-opacity duration-300"
          style={{ 
            bottom: isWorkoutHighlight && hasWorkoutPlan ? '8rem' : (isNavHighlight ? '4rem' : '0')
          }}
          onClick={blockClick}
        />
      )}
      
      {/* Chat Input highlight - dynamically positioned using getBoundingClientRect */}
      {isChatInputHighlight && chatInputRect && (
        <>
          {/* Dark overlay above chat input */}
          <div 
            className="fixed inset-x-0 top-0 bg-black/70 z-[100]"
            style={{ height: chatInputRect.top - 4 }}
            onClick={blockClick}
          />
          {/* Dark overlay below chat input */}
          <div 
            className="fixed inset-x-0 bottom-0 bg-black/70 z-[100]"
            style={{ height: window.innerHeight - chatInputRect.bottom - 4 }}
            onClick={blockClick}
          />
          {/* Left overlay */}
          <div 
            className="fixed bg-black/70 z-[100]"
            style={{
              top: chatInputRect.top - 4,
              left: 0,
              width: Math.max(0, chatInputRect.left - 4),
              height: chatInputRect.height + 8,
            }}
            onClick={blockClick}
          />
          {/* Right overlay */}
          <div 
            className="fixed bg-black/70 z-[100]"
            style={{
              top: chatInputRect.top - 4,
              right: 0,
              width: Math.max(0, window.innerWidth - chatInputRect.right - 4),
              height: chatInputRect.height + 8,
            }}
            onClick={blockClick}
          />
          {/* Glow ring - exact position around chat input */}
          <div 
            className="fixed z-[120] pointer-events-none"
            style={{ 
              top: chatInputRect.top - 4,
              left: chatInputRect.left - 4,
              width: chatInputRect.width + 8,
              height: chatInputRect.height + 8,
            }}
          >
            <div className="w-full h-full rounded-xl border-2 border-primary shadow-[0_0_25px_hsl(var(--primary)/0.6)] animate-pulse" />
          </div>
        </>
      )}
      
      {/* Element highlight (for workout plan, nutrition plan, PR section) */}
      {isElementHighlight && elementRect && (
        <>
          {(() => {
            const buffer = 8;
            const glowBuffer = 4;
            // Calculate how much space is at the bottom (nav + possibly workout button)
            const bottomReserve = hasWorkoutPlan ? 128 : 64; // 8rem or 4rem in pixels
            return (
              <>
                {/* Top overlay */}
                <div 
                  className="fixed bg-black/70 z-[100] transition-all duration-300"
                  style={{
                    top: 0,
                    left: 0,
                    right: 0,
                    height: Math.max(0, elementRect.top - buffer),
                    pointerEvents: step.showTapToExplore ? 'none' : 'auto',
                  }}
                  onClick={step.showTapToExplore ? undefined : blockClick}
                />
                {/* Bottom overlay - stops above the nav/workout button */}
                <div 
                  className="fixed bg-black/70 z-[100] transition-all duration-300"
                  style={{
                    top: elementRect.bottom + buffer,
                    left: 0,
                    right: 0,
                    bottom: bottomReserve,
                    pointerEvents: step.showTapToExplore ? 'none' : 'auto',
                  }}
                  onClick={step.showTapToExplore ? undefined : blockClick}
                />
                {/* Left overlay */}
                <div 
                  className="fixed bg-black/70 z-[100] transition-all duration-300"
                  style={{
                    top: elementRect.top - buffer,
                    left: 0,
                    width: Math.max(0, elementRect.left - buffer),
                    height: elementRect.height + (buffer * 2),
                    pointerEvents: step.showTapToExplore ? 'none' : 'auto',
                  }}
                  onClick={step.showTapToExplore ? undefined : blockClick}
                />
                {/* Right overlay */}
                <div 
                  className="fixed bg-black/70 z-[100] transition-all duration-300"
                  style={{
                    top: elementRect.top - buffer,
                    right: 0,
                    width: Math.max(0, window.innerWidth - elementRect.right - buffer),
                    height: elementRect.height + (buffer * 2),
                    pointerEvents: step.showTapToExplore ? 'none' : 'auto',
                  }}
                  onClick={step.showTapToExplore ? undefined : blockClick}
                />
                {/* Glow ring around element */}
                <div 
                  className="fixed z-[120] pointer-events-none transition-all duration-300"
                  style={{
                    top: elementRect.top - glowBuffer,
                    left: elementRect.left - glowBuffer,
                    width: elementRect.width + (glowBuffer * 2),
                    height: elementRect.height + (glowBuffer * 2),
                  }}
                >
                  <div className="w-full h-full rounded-xl border-2 border-primary shadow-[0_0_25px_hsl(var(--primary)/0.6)] animate-pulse" />
                </div>
              </>
            );
          })()}
        </>
      )}
      
      {/* Workout button highlight area - dynamically positioned */}
      {isWorkoutHighlight && hasWorkoutPlan && workoutButtonRect && (
        <>
          {/* Dark overlay above workout button */}
          <div 
            className="fixed inset-x-0 top-0 bg-black/70 z-[100]"
            style={{ height: workoutButtonRect.top - 8 }}
            onClick={blockClick}
          />
          {/* Dark overlay below workout button (covers nav) */}
          <div 
            className="fixed inset-x-0 bottom-0 bg-black/70 z-[100]"
            style={{ height: window.innerHeight - workoutButtonRect.bottom - 8 }}
            onClick={blockClick}
          />
          {/* Left overlay */}
          <div 
            className="fixed bg-black/70 z-[100]"
            style={{
              top: workoutButtonRect.top - 8,
              left: 0,
              width: Math.max(0, workoutButtonRect.left - 8),
              height: workoutButtonRect.height + 16,
            }}
            onClick={blockClick}
          />
          {/* Right overlay */}
          <div 
            className="fixed bg-black/70 z-[100]"
            style={{
              top: workoutButtonRect.top - 8,
              right: 0,
              width: Math.max(0, window.innerWidth - workoutButtonRect.right - 8),
              height: workoutButtonRect.height + 16,
            }}
            onClick={blockClick}
          />
          {/* Clickable workout button area - starts workout and ends tour */}
          <div 
            className="fixed z-[101] cursor-pointer"
            style={{ 
              top: workoutButtonRect.top,
              left: workoutButtonRect.left,
              width: workoutButtonRect.width,
              height: workoutButtonRect.height,
            }}
            onClick={() => {
              if (onWorkoutStart) {
                onWorkoutStart();
                onComplete();
              }
            }}
          />
          {/* Enhanced glow around workout button with scale animation */}
          <div 
            className="fixed z-[120] pointer-events-none animate-[scale-pulse_1.2s_ease-in-out_infinite]"
            style={{ 
              top: workoutButtonRect.top - 4,
              left: workoutButtonRect.left - 4,
              width: workoutButtonRect.width + 8,
              height: workoutButtonRect.height + 8,
            }}
          >
            <div className="w-full h-full rounded-xl border-2 border-primary shadow-[0_0_30px_hsl(var(--primary)/0.8),0_0_60px_hsl(var(--primary)/0.4)]" />
          </div>
        </>
      )}
      
      {/* Workout button overlay when not highlighting it */}
      {!isWorkoutHighlight && !isElementHighlight && !isChatInputHighlight && hasWorkoutPlan && (
        <div 
          className="fixed inset-x-0 h-16 bg-black/70 z-[100]"
          style={{ bottom: '4rem' }}
          onClick={blockClick}
        />
      )}
      
      {/* Bottom nav overlay with gap for spotlight */}
      {isNavHighlight && spotlightLeft !== null && (
        <>
          {/* Left dark section */}
          <div 
            className="fixed bottom-0 h-16 bg-black/70 z-[100]"
            style={{ 
              left: 0,
              width: `calc(${spotlightLeft}% - 2.5rem)`,
            }}
            onClick={blockClick}
          />
          
          {/* Right dark section */}
          <div 
            className="fixed bottom-0 h-16 bg-black/70 z-[100]"
            style={{ 
              right: 0,
              width: `calc(${100 - spotlightLeft}% - 2.5rem)`,
            }}
            onClick={blockClick}
          />
          
          {/* Glowing ring indicator */}
          <div 
            className="fixed bottom-0 h-16 z-[120] pointer-events-none flex items-center justify-center"
            style={{ 
              left: `calc(${spotlightLeft}% - 2.5rem)`,
              width: '5rem',
            }}
          >
            <div className="w-16 h-16 rounded-full border-2 border-primary shadow-[0_0_25px_hsl(var(--primary)/0.6)] animate-pulse" />
          </div>
        </>
      )}
      
      {/* Full bottom nav overlay when no nav highlight */}
      {!isNavHighlight && !isElementHighlight && !isChatInputHighlight && (
        <div 
          className="fixed inset-x-0 bottom-0 h-16 bg-black/70 z-[100]"
          onClick={blockClick}
        />
      )}
      
      {/* Tour Card */}
      <div 
        className={`fixed left-4 right-4 z-[200] transition-all duration-300 pointer-events-auto ${isAnimating || isRepositioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
        style={{
          ...cardStyle,
          maxWidth: '28rem',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <div className="glass-card rounded-2xl p-5 space-y-4 border border-border/50 shadow-2xl">
          {/* Close button */}
          <button 
            onClick={handleSkip}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5">
            {TOUR_STEPS.map((_, index) => (
              <div
                key={index}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === currentStep 
                    ? 'w-4 bg-primary' 
                    : index < currentStep 
                      ? 'w-1.5 bg-primary/50' 
                      : 'w-1.5 bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Icon & Title row */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              {step.icon}
            </div>
            <h2 className="font-display text-lg font-bold leading-tight">{step.title}</h2>
          </div>

          {/* Description */}
          <p className="text-muted-foreground text-sm leading-relaxed">
            {step.description}
          </p>

          {/* Arrow pointing to highlighted element - only show when step has showTapToExplore */}
          {step.showTapToExplore && (isNavHighlight || isElementHighlight || (isWorkoutHighlight && hasWorkoutPlan)) && (
            <div className="flex justify-center pt-1">
              <div className="flex items-center gap-2 text-primary text-xs font-medium animate-bounce">
                <span>{arrowPointsDown ? '↓' : '↑'}</span>
                <span>{isWorkoutHighlight ? 'Tap to start a workout' : 'Tap to explore'}</span>
                <span>{arrowPointsDown ? '↓' : '↑'}</span>
              </div>
            </div>
          )}
          
          {/* Note when workout button isn't visible */}
          {isWorkoutHighlight && !hasWorkoutPlan && (
            <div className="text-center">
              <p className="text-xs text-muted-foreground italic">
                Ask me for a workout plan in chat to see this button!
              </p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              disabled={isFirstStep}
              className={isFirstStep ? 'invisible' : ''}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <Button
              variant="evolve"
              size="sm"
              onClick={handleNext}
              className="min-w-[90px]"
            >
              {isLastStep ? "Let's Go!" : 'Next'}
              {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>

          {/* Skip link */}
          {!isLastStep && (
            <div className="text-center">
              <button 
                onClick={handleSkip}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Skip tour
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
